import * as PropTypes from "prop-types";

import * as piasu from "../NonComponents/PIAppStateUtil";
import * as pisc from "../NonComponents/PIServerConst";
import * as pic from "../NonComponents/PIConst";

import { RS } from "../../../data/strings/global";
import * as SC from "../../../data/strings/PIStringConst";

import TButton from "../../common/TButton";
import * as Theme from "../../../app/Theme";

import { promisify } from "../../../utilities";

import { getProjDefaultsAsync } from "../../../api/server_calls";
import { onCalculateAsync } from "../NonComponents/PICalc";

import { includeCostList } from "../Tables/PICostCatLiteTable";
import { defaultAboveSiteCostCategories } from "../Tables/PICostCatAboveSiteTable";

// Note: This object is also used to determine what keys to copy when resetting to default
const defaultCategory = {
  monthlyCost: 0,
  ARVs: 0,
  adherenceSupport: 0,
  initiation: {
    personnel: 0,
    visitLabs: 0,
    recurrent: 0,
    capital: 0,
    total: 0,
  },
  continuation: {
    personnel: 0,
    visitLabs: 0,
    recurrent: 0,
    capital: 0,
    total: 0,
  },
  annual: 0,
};

// mutate modVarObjList to reset cost categories to original values for all methods
function resetCostCategories(defModVars, modVarObjList) {
  const curCatLite = piasu.getModVarValue(modVarObjList, "PI_CostCategories_Lite");
  const defCatLite = piasu.getModVarValue(defModVars, "PI_CostCategories_Lite");

  for (const method of curCatLite) {
    if (method.categories === undefined || method.categories.length === 0) {
      console.error(`Method ${method.mstID} has no categories`);
      continue;
    }

    if (method.mstID === pisc.includedCCLite) {
      continue;
    }

    const defMethod = defCatLite.find((v) => v.mstID === method.mstID);
    for (const cat of method.categories) {
      const defCat = defMethod?.categories?.find((v) => v.mstID === cat.mstID) ?? defaultCategory;

      for (const k in defaultCategory) {
        cat[k] = defCat[k];
      }
    }

    let costCatObjList = piasu.getModVarValue(modVarObjList, pisc.costCategoriesLiteMVTag);
    for (const id in includeCostList) {
      if (id === includeCostList["skip"]) {
        continue;
      }
      piasu.setCostIncludedCostsLite(includeCostList[id], method.mstID, costCatObjList, true);
    }
  }
}

// mutate modVarObjList to zero out above site cost values and re-add deleted categories.
// custom and renamed original (no mstID to distinguish) categories and their values are retained.
function resetAboveSiteCosts(defModVars, modVarObjList) {
  const defaultCosts = defaultAboveSiteCostCategories();

  const aboveSiteCosts = piasu.getModVarValue(modVarObjList, "PI_ProgramWideAnnualAboveSiteCosts");
  let aboveSiteCostsByMethod = piasu.getModVarValue(modVarObjList, pisc.annualLumpSumMVTag);

  // Units cannot be reset as user may have custom categories defined with values we need to
  // retain and must not be converted to another unit.
  // aboveSiteCosts.units = defaultCosts.units;

  aboveSiteCosts.categories = aboveSiteCosts.categories.map((category) => {
    const defCost = defaultCosts.categories.find((c) => c.name === category.name);
    if (defCost === undefined) return { ...category };

    // Ignore any default value and always reset to 0 as any non zero value is
    // unit specific and must not be converted.
    return {
      name: defCost.name,
      costs: defCost.costs.map((c) => ({ ...c, value: 0 })),
    };
  });

  // re-add deleted default costs in original location
  for (const [defCostIdx, defCost] of defaultCosts.categories.entries()) {
    if (aboveSiteCosts.categories.length >= pic.MaxAboveSiteLumpSumCostCategories) break;

    if (aboveSiteCosts.categories.find((v) => v.name === defCost.name)) continue;

    aboveSiteCosts.categories.splice(defCostIdx, 0, {
      name: defCost.name,
      costs: defCost.costs.map((c) => ({ ...c, value: 0 })),
    });
  }

  const newAboveSiteCostsByMethod = aboveSiteCostsByMethod.map((_) => 0);
  piasu.setModVarValue(modVarObjList, pisc.annualLumpSumMVTag, newAboveSiteCostsByMethod);
}

const PIResetDefaultCostsBtn = (props) => {
  const onModVarsChange = promisify(props.onModVarsChange);
  const onCalculatingChange = promisify(props.onCalculatingChange);

  const onResetClick = async () => {
    try {
      await onCalculatingChange(true);

      const modVarObjList = structuredClone(props.modVarObjList);

      // Fetch defaults from server, inc. hoop jumping
      const pseMode = piasu.isPSEMode(props.modVarObjList);
      const countryCode = piasu.getModVarValue(modVarObjList, "PI_CountryISO");
      const { modvars: defaults } = await getProjDefaultsAsync({ countryCode, pse: pseMode });

      const defModVars = await onCalculateAsync(defaults, "", props.onDialogChange);

      // Adjust methods to include any changes in the session
      const methods = piasu.getModVarValue(modVarObjList, "PI_Methods");
      let defMethods = piasu.getModVarValue(defModVars, "PI_Methods");
      defMethods = defMethods.map((v) => methods.find((m) => m.mstID === v.mstID) ?? v);
      piasu.setModVarValue(defModVars, "PI_Methods", defMethods);

      piasu.setCostCategoriesLiteValues(defModVars, new Set(defMethods.map((v) => v.mstID)));

      resetCostCategories(defModVars, modVarObjList);
      resetAboveSiteCosts(defModVars, modVarObjList);

      // Re-calculate to fix anything caused by the above
      const newModVars = await onCalculateAsync(modVarObjList, "", props.onDialogChange);

      // Update mod vars
      await onModVarsChange(newModVars, true);
    } catch (err) {
      console.log(err);
    } finally {
      await onCalculatingChange(false);
    }
  };

  return (
    <TButton
      caption={RS(SC.GB_stResetToDefault)}
      key={"defaultCostsBtn"}
      onClick={onResetClick}
      containerStyle={{
        display: "inline-block",
        marginRight: 10,
        marginTop: 0,
      }}
      style={{
        backgroundColor: Theme.PI_TertiaryColor,
        padding: 0,
      }}
      {...props}
    />
  );
};

PIResetDefaultCostsBtn.propTypes = {
  modVarObjList: PropTypes.array.isRequired,
  onModVarsChange: PropTypes.func,
  onDialogChange: PropTypes.func,
  onCalculatingChange: PropTypes.func,
};

PIResetDefaultCostsBtn.defaultProps = {
  onModVarsChange: () => {},
  onDialogChange: () => {},
  onCalculatingChange: () => {},
};

export default PIResetDefaultCostsBtn;
